import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import ProduceCard from '@/components/ProduceCard';
import RequirementCard from '@/components/RequirementCard';
import { Input } from '@/components/ui/input';
import { Search, SlidersHorizontal, X, Sprout, ChevronDown, Download, Share2, ShoppingBag, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { trackSearch, trackSave, trackPageView } from '@/lib/analytics';

function FeedInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('frootex_install_dismissed') === '1');

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    setIsInstalled(standalone);
    if (standalone) return;

    const ios = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()) && !window.MSStream;
    setIsIOS(ios);

    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) { deferredPrompt.prompt(); await deferredPrompt.userChoice; setDeferredPrompt(null); setIsInstalled(true); }
  };

  const handleDismiss = () => {
    localStorage.setItem('frootex_install_dismissed', '1');
    setDismissed(true);
  };

  // Hide banner if installed or previously dismissed. User can still install via Profile menu.
  if (isInstalled || dismissed) return null;

  // Not installed — show premium Download CTA with a dismiss option
  return (
    <div data-testid="download-app-cta" className="relative bg-[#F6F8FB] rounded-3xl overflow-hidden animate-fade-in-up stagger-1 border border-[#E4E8EF]">
      {/* Decorative ambient glow */}
      <div aria-hidden className="absolute -top-16 -right-16 w-40 h-40 bg-[#0B4F3F]/10 rounded-full blur-3xl" />
      <div aria-hidden className="absolute -bottom-20 -left-10 w-40 h-40 bg-[#0B4F3F]/[0.06] rounded-full blur-3xl" />

      <button
        data-testid="dismiss-install-banner"
        onClick={handleDismiss}
        aria-label="Dismiss install banner"
        className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-[#F6F8FB]/50 hover:bg-[#F6F8FB]/80 flex items-center justify-center text-[#5B6675] hover:text-[#0B1620] transition-colors z-10"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* Top section */}
      <div className="relative p-5 pb-3 pr-10">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#0B4F3F] flex items-center justify-center flex-shrink-0 shadow-[0_6px_20px_-6px_rgba(11,79,63,0.6)]">
            <Download className="w-6 h-6 text-[#F6F8FB]" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <p className="text-base font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight">Get the Frootex App</p>
            <p className="text-xs text-[#5B6675] mt-0.5">Faster, offline-ready, feels native.</p>
          </div>
        </div>
      </div>

      {/* Features row */}
      <div className="relative px-5 pb-3 flex gap-4 text-[10px] text-[#5B6675]">
        <span className="flex items-center gap-1"><Download className="w-3 h-3 text-[#0B4F3F]" /> Faster</span>
        <span className="flex items-center gap-1"><Sprout className="w-3 h-3 text-[#0B4F3F]" /> Offline</span>
        <span className="flex items-center gap-1"><Share2 className="w-3 h-3 text-[#0B4F3F]" /> Native</span>
      </div>

      {/* Action area */}
      <div className="relative px-5 pb-5">
        {deferredPrompt ? (
          <button
            onClick={handleInstall}
            data-testid="download-app-btn"
            className="w-full h-11 bg-[#0B4F3F] hover:bg-[#0E5D4A] text-[#F6F8FB] text-sm font-bold rounded-full active:scale-[0.98] transition-all shadow-[0_6px_20px_-6px_rgba(11,79,63,0.5)] flex items-center justify-center gap-2"
          >
            <Download className="w-4 h-4" /> Install Now — It's Free
          </button>
        ) : isIOS ? (
          <div className="bg-[#EFF2F7] border border-[#E4E8EF] rounded-2xl p-3 text-center">
            <p className="text-xs text-[#0B1620] font-medium">How to install on iPhone</p>
            <p className="text-[11px] text-[#5B6675] mt-1">
              Tap <Share2 className="w-3.5 h-3.5 inline mx-0.5 text-[#0B4F3F]" /> in Safari, then <span className="font-semibold text-[#0B1620]">"Add to Home Screen"</span>
            </p>
          </div>
        ) : (
          <div className="bg-[#EFF2F7] border border-[#E4E8EF] rounded-2xl p-3 text-center">
            <p className="text-xs text-[#0B1620] font-medium">Open in Chrome to install</p>
            <p className="text-[11px] text-[#5B6675] mt-1">Chrome will show an install prompt automatically</p>
          </div>
        )}
      </div>
    </div>
  );
}

const CATEGORIES = [
  { id: 'all', name: 'All' },
  { id: 'vegetables', name: 'Vegetables' },
  { id: 'fruits', name: 'Fruits' },
  { id: 'grains', name: 'Grains' },
  { id: 'pulses', name: 'Pulses' },
  { id: 'spices', name: 'Spices' },
  { id: 'dairy', name: 'Dairy' },
  { id: 'oilseeds', name: 'Oilseeds' },
  { id: 'other', name: 'Other' },
];

const SORT_OPTIONS = [
  { id: 'newest', label: 'Newest First' },
  { id: 'price_low', label: 'Price: Low to High' },
  { id: 'price_high', label: 'Price: High to Low' },
];

const FEED_TYPE_KEY = 'frootex_feed_type';

// Feed types shown in the header pills. 'all' is the default and merges both
// produce + requirements into a single chronological list.
const FEED_TYPES = [
  { id: 'all', label: 'All', Icon: LayoutGrid, testId: 'feed-type-all' },
  { id: 'produce', label: 'Produce', Icon: Sprout, testId: 'feed-type-produce' },
  { id: 'requirements', label: 'Requirement', Icon: ShoppingBag, testId: 'feed-type-requirements' },
];

export default function FeedPage() {
  const { api, user } = useAuth();
  const [feedType, setFeedType] = useState(() => {
    const s = localStorage.getItem(FEED_TYPE_KEY);
    if (s === 'produce' || s === 'requirements' || s === 'all') return s;
    return 'all';
  });
  const [posts, setPosts] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    localStorage.setItem(FEED_TYPE_KEY, feedType);
  }, [feedType]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort_by: sortBy };
      if (search) params.search = search;
      if (category !== 'all') params.category = category;

      if (feedType === 'all') {
        // Fetch both lists in parallel, tag each post with a kind, then merge
        // chronologically. Pagination is disabled in this mode for simplicity.
        const [produceRes, reqRes] = await Promise.all([
          api.get('/produce', { params: { ...params, limit: 20 } }),
          api.get('/requirements', { params: { ...params, limit: 20 } }),
        ]);
        const merged = [
          ...(produceRes.data.posts || []).map(p => ({ ...p, __kind: 'produce' })),
          ...(reqRes.data.posts || []).map(p => ({ ...p, __kind: 'requirement' })),
        ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setPosts(merged);
        setTotalPages(1);
      } else {
        const endpoint = feedType === 'requirements' ? '/requirements' : '/produce';
        const { data } = await api.get(endpoint, { params });
        setPosts((data.posts || []).map(p => ({ ...p, __kind: feedType === 'requirements' ? 'requirement' : 'produce' })));
        setTotalPages(data.pages || 1);
      }
    } catch {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [api, page, search, category, sortBy, feedType]);

  const fetchSavedIds = useCallback(async () => {
    try {
      const { data } = await api.get('/saved/ids');
      setSavedIds(new Set(data.post_ids || []));
    } catch {}
  }, [api]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    fetchSavedIds();
  }, [fetchSavedIds]);

  const handleToggleSave = async (postId) => {
    try {
      const { data } = await api.post(`/saved/${postId}`);
      setSavedIds(prev => {
        const next = new Set(prev);
        if (data.saved) next.add(postId);
        else next.delete(postId);
        return next;
      });
      trackSave(postId, data.saved);
      toast.success(data.saved ? 'Saved!' : 'Removed from saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    if (search) trackSearch(search, category);
    fetchPosts();
  };

  return (
    <AppShell
      title={
        <div className="flex items-center gap-2">
          <img src="/frootex-logo.png" alt="Frootex" className="h-7" />
        </div>
      }
    >
      <div className="p-5 space-y-5" data-testid="feed-page">
        {/* Welcome */}
        {user && (
          <div className="animate-fade-in-up">
            <p className="text-xs uppercase tracking-[0.15em] text-[#5B6675]">Welcome back</p>
            <h2 className="text-3xl font-bold text-[#0B1620] font-['Bricolage_Grotesque'] tracking-tight mt-1">
              {user.name}<span className="text-[#0B4F3F]">.</span>
            </h2>
          </div>
        )}

        {/* Install App CTA */}
        <FeedInstallBanner />

        {/* Feed type toggle: All / Produce / Requirement */}
        <div
          role="tablist"
          data-testid="feed-type-toggle"
          className="relative grid grid-cols-3 gap-1 p-1 bg-[#EFF2F7] rounded-full border border-[#E4E8EF] animate-fade-in-up stagger-1"
        >
          {FEED_TYPES.map(({ id, label, Icon, testId }) => {
            const active = feedType === id;
            return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={testId}
                onClick={() => { setFeedType(id); setPage(1); }}
                className={`relative z-10 h-10 rounded-full flex items-center justify-center gap-1.5 text-[12px] font-semibold transition-all duration-200 ${
                  active
                    ? 'bg-[#F6F8FB] text-[#0B1620] shadow-[0_2px_8px_-2px_rgba(11,22,32,0.08)]'
                    : 'text-[#5B6675] hover:text-[#0B1620]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="relative animate-fade-in-up stagger-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5B6675]" />
          <Input
            data-testid="search-input"
            placeholder="Search produce, farmers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 pr-10 h-12 bg-[#F1F5FA] border-[#E4E8EF] rounded-full text-[#0B1620] placeholder:text-[#5B6675]/70 focus:ring-1 focus:ring-[#0B4F3F]/60 focus:border-[#0B4F3F]/60"
          />
          {search && (
            <button
              type="button"
              onClick={() => { setSearch(''); setPage(1); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#EFF2F7] flex items-center justify-center hover:bg-[#E4E8EF]"
            >
              <X className="w-3.5 h-3.5 text-[#5B6675]" />
            </button>
          )}
        </form>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 animate-fade-in-up stagger-2 -mx-1 px-1">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              data-testid={`category-${cat.id}`}
              onClick={() => { setCategory(cat.id); setPage(1); }}
              className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all duration-200 border ${
                category === cat.id
                  ? 'bg-[#0B4F3F] text-[#F6F8FB] border-[#0B4F3F] shadow-[0_4px_16px_-4px_rgba(11,79,63,0.4)]'
                  : 'bg-[#F6F8FB] text-[#5B6675] border-[#E4E8EF] hover:text-[#0B1620] hover:border-[#0B4F3F]/20'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex items-center justify-between animate-fade-in-up stagger-3">
          <p className="text-xs text-[#5B6675]">
            {loading ? (
              <span className="inline-flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#0B4F3F] animate-pulse" /> Loading…</span>
            ) : (
              <span><span className="text-[#0B1620] font-semibold">{posts.length}</span> results</span>
            )}
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="sort-trigger" className="flex items-center gap-1.5 text-xs text-[#5B6675] hover:text-[#0B4F3F] transition-colors px-3 py-1.5 rounded-full border border-[#E4E8EF] bg-[#F6F8FB]">
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {SORT_OPTIONS.find(s => s.id === sortBy)?.label}
              <ChevronDown className="w-3 h-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#F6F8FB] border-[#E4E8EF] rounded-2xl">
              {SORT_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.id}
                  data-testid={`sort-${opt.id}`}
                  onClick={() => { setSortBy(opt.id); setPage(1); }}
                  className={`rounded-lg ${opt.id === sortBy ? 'text-[#0B4F3F] font-semibold' : 'text-[#0B1620]'}`}
                >
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* KYC Banner for vendors */}
        {user?.role === 'vendor' && !user?.is_verified && (
          <div
            data-testid="kyc-banner"
            className="relative bg-[#F6F8FB] border border-[#0B4F3F]/25 rounded-2xl p-4 flex items-center gap-3 animate-fade-in-up stagger-3 overflow-hidden hover:border-[#0B4F3F]/50 transition-colors"
          >
            <div aria-hidden className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-[#0B4F3F]/10 blur-2xl" />
            <div className="relative w-11 h-11 rounded-2xl bg-[#0B4F3F]/15 flex items-center justify-center flex-shrink-0 border border-[#0B4F3F]/30">
              <Sprout className="w-5 h-5 text-[#0B4F3F]" />
            </div>
            <div className="relative flex-1 min-w-0">
              <p className="text-sm font-semibold text-[#0B1620]">Become a Farmer</p>
              <p className="text-xs text-[#5B6675] mt-0.5">Get verified to sell your produce directly</p>
            </div>
            <button
              data-testid="kyc-register-btn"
              onClick={(e) => { e.stopPropagation(); window.location.href = '/kyc-apply'; }}
              className="relative flex-shrink-0 h-8 px-3.5 rounded-full text-[12px] font-semibold text-[#0B4F3F] bg-transparent hover:bg-[#0B4F3F]/8 active:bg-[#0B4F3F]/12 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0B4F3F]/30"
            >
              Register
            </button>
          </div>
        )}

        {/* Posts */}
        <div className="space-y-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-[#F6F8FB] rounded-3xl border border-[#E4E8EF] overflow-hidden">
                <div className="w-full h-52 skeleton" />
                <div className="p-5 space-y-3">
                  <div className="h-4 skeleton rounded w-3/4" />
                  <div className="h-3 skeleton rounded w-full" />
                  <div className="h-3 skeleton rounded w-1/2" />
                </div>
              </div>
            ))
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-[#F6F8FB] border border-[#E4E8EF] flex items-center justify-center mb-4">
                {feedType === 'requirements' ? (
                  <ShoppingBag className="w-7 h-7 text-[#0B4F3F]/60" />
                ) : feedType === 'produce' ? (
                  <Sprout className="w-7 h-7 text-[#0B4F3F]/60" />
                ) : (
                  <LayoutGrid className="w-7 h-7 text-[#0B4F3F]/60" />
                )}
              </div>
              <p className="text-[#0B1620] text-sm font-semibold">
                {feedType === 'requirements'
                  ? 'No open requirements'
                  : feedType === 'produce'
                  ? 'No produce found'
                  : 'Nothing to show yet'}
              </p>
              <p className="text-xs text-[#5B6675] mt-1">
                {feedType === 'requirements'
                  ? 'Be the first to post what you need'
                  : 'Try different filters or search terms'}
              </p>
            </div>
          ) : (
            posts.map((post, i) => (
              post.__kind === 'requirement' ? (
                <RequirementCard key={`r-${post.id}`} post={post} index={i} />
              ) : (
                <ProduceCard
                  key={`p-${post.id}`}
                  post={post}
                  index={i}
                  isSaved={savedIds.has(post.id)}
                  onToggleSave={handleToggleSave}
                />
              )
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 py-4">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
              className="px-4 py-2 rounded-full text-xs font-semibold bg-[#F6F8FB] border border-[#E4E8EF] text-[#0B1620] hover:border-[#0B4F3F]/30 disabled:opacity-40 disabled:hover:border-[#E4E8EF] transition-colors"
            >
              Previous
            </button>
            <span className="text-xs text-[#5B6675] tabular-nums">{page} / {totalPages}</span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="px-4 py-2 rounded-full text-xs font-semibold bg-[#F6F8FB] border border-[#E4E8EF] text-[#0B1620] hover:border-[#0B4F3F]/30 disabled:opacity-40 disabled:hover:border-[#E4E8EF] transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </AppShell>
  );
}
